import { Box, Chip, Grid, Link, Typography } from "@mui/material";
import { OrganizationUserContext } from "contexts/Organization";
import { OutreachTemplatesContext } from "contexts/OutreachTemplates";
import { useContext } from "react";
import { routes } from "schemas/routes";

import StyledLargeButton from "components/Styled/Button/StyledLargeButton";
import { trackEvent } from "utils/tracking";

import styles from "./styles";

interface Props {
  title: string;
  description: string;
  infoBox: string | JSX.Element;
  completed?: boolean;
}

const CreateNicheTemplateCard = ({
  title,
  description,
  infoBox,
  completed,
}: Props) => {
  const { currentOrg } = useContext(OrganizationUserContext);
  const { setCreateTemplateDialogOpen, outreachTemplates } = useContext(
    OutreachTemplatesContext,
  );

  const getChipLabel = () => {
    if (currentOrg?.isVaUser) {
      return completed ? "$10" : "Earn $10";
    } else if (currentOrg?.isProUser) {
      return completed ? (
        <>
          <Box component="i" className="fa-regular fa-check" sx={{ mr: 0.5 }} />{" "}
          Completed
        </>
      ) : (
        "Highly recommended"
      );
    } else {
      return completed ? (
        <>
          <Box component="i" className="fa-regular fa-check" sx={{ mr: 0.5 }} />{" "}
          50 extra email credits added
        </>
      ) : (
        <>Receive extra 50 email credits</>
      );
    }
  };

  return (
    <Box sx={styles.card(completed)}>
      <Box sx={{ display: "flex", alignItems: "center", gap: "12px", mb: 2 }}>
        <Box sx={styles.logoContainer(completed)}>
          <Box
            component="i"
            className="fa-regular fa-file"
            sx={{ color: completed ? "white" : "#7C86FF", fontSize: 20 }}
          />
        </Box>

        <Box
          sx={{
            display: { xs: "auto", md: "flex" },
            flexDirection: "column",
            gap: "4px",
            flex: 1,
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              display: { xs: "auto", md: "flex" },
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <Typography fontWeight={700}>{title}</Typography>
            <Chip
              label={getChipLabel()}
              size="small"
              sx={styles.chip(completed)}
            />
          </Box>
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>
        </Box>
      </Box>

      <Box sx={styles.infoBox}>
        <Grid item xs="auto">
          <Box
            component="i"
            className="fa-regular fa-star"
            sx={styles.infoBoxIcon}
          />
        </Grid>

        <Grid item xs>
          <Typography>{infoBox}</Typography>
        </Grid>
      </Box>

      {completed ? (
        <>
          <Typography sx={{ color: "#014339", mb: 2 }}>
            <Box
              component="i"
              className="fa-solid fa-circle-check"
              sx={{ color: "green.main", mr: 1 }}
            />
            Niche template created!
          </Typography>
          Visit the{" "}
          <Link
            sx={{
              fontSize: 12,
              mt: 2,
              textDecoration: "none",
              fontWeight: 600,
            }}
            href={`/${routes.templates}`}
          >
            Templates tab
          </Link>{" "}
          to make edits
        </>
      ) : (
        <>
          <StyledLargeButton
            styles={{
              backgroundColor: "black",
              color: "white",
            }}
            fullWidth
            onClick={() => {
              trackEvent("Gamification - Create Niche Template");
              setCreateTemplateDialogOpen(true);
            }}
            disabled={outreachTemplates.length === 0}
          >
            Create Niche Template ✍🏼
          </StyledLargeButton>

          {outreachTemplates?.length === 0 && (
            <Typography sx={{ mt: 2, color: "#9CA3AF" }}>
              <Box
                component="i"
                className="fa-solid fa-lock"
                sx={{ fontSize: "14px", color: "#9CA3AF", mr: 1 }}
              />
              Please{" "}
              <Link
                sx={{
                  textDecoration: "none",
                  cursor: "pointer",
                  color: "primary.main",
                  fontWeight: 700,
                }}
                onClick={() => {
                  trackEvent("Gamification - Create First Template");
                  setCreateTemplateDialogOpen(true);
                }}
              >
                create a general template first
              </Link>{" "}
              to unlock this challenge.
            </Typography>
          )}
        </>
      )}
    </Box>
  );
};

export default CreateNicheTemplateCard;
